import { datetime } from "src/utils";
import { WebsiteSystemAuditAction } from "src/services/websiteSystem";
import { DAY_LABEL } from "./formatPeriod";
import { WEBSITE_PRODUCT_LABEL } from "./WebsiteLogos";

export const AUDIT_ACTION_LABEL: Record<WebsiteSystemAuditAction, string> = {
  create: "สร้าง",
  edit: "แก้ไข",
  on: "เปิดใช้งาน",
  off: "ปิดใช้งาน",
  delete: "ลบ",
  auto_off: "ปิดอัตโนมัติ (หมดเวลา)",
};

export const formatAuditAction = (action: string): string =>
  AUDIT_ACTION_LABEL[action as WebsiteSystemAuditAction] || action;

// The three sites use the table's own labels; ALL is a fourth value that only
// ever appears on a Tip/Rain audit row (that code's single row is product=ALL) -
// see src/services/websiteSystem.
export const AUDIT_PRODUCT_LABEL: Record<string, string> = {
  ...WEBSITE_PRODUCT_LABEL,
  ALL: "ทุกเว็บไซต์",
};

export const formatAuditProduct = (product: string): string => AUDIT_PRODUCT_LABEL[product] || product;

// created_at is naive-UTC (no timezone suffix) - same convention as every other
// backend timestamp this page reads (start_datetime/end_datetime/updated_at), and
// the same assumption src/components/Blacklist/BlacklistTable.tsx already makes
// for its own created_at column via the same datetime() helper.
export const formatAuditCreatedAt = (value: string): string => datetime(value).format("DD/MM/YYYY HH:mm:ss");

// "system" is the fixed actor name the backend's sweep writes for an auto_off row
// (there is no admin behind it) - everything else is either a resolved admin name
// or, if the uid did not resolve to a User row, the raw uid string itself.
export const formatAuditActorName = (actorName: string | null): string => {
  if (!actorName) return "-";
  if (actorName === "system") return "ระบบ (อัตโนมัติ)";
  return actorName;
};

export const AUDIT_FIELD_LABEL: Record<string, string> = {
  title: "หัวข้อ",
  message: "ข้อความ",
  link_url: "ลิงก์",
  link_label: "คำขยายลิงก์",
  is_enable: "สถานะ",
  is_selected: "เว็บที่แสดง",
  type_time: "รูปแบบเวลาที่แสดง",
  days_week: "วันที่แสดง",
  start_datetime: "เวลาเริ่มต้น",
  end_datetime: "เวลาสิ้นสุด",
  is_toast: "ป้ายข้อความหน้าเว็บ",
  is_notification: "กล่องข้อความแจ้งเตือน",
};

export const formatAuditFieldLabel = (field: string): string => AUDIT_FIELD_LABEL[field] || field;

// Same wording as the edit modal's own period options.
const TYPE_TIME_LABEL: Record<string, string> = {
  now: "ทันที",
  schedule: "กำหนดวันและเวลา",
  custom: "กำหนดระยะเวลาแบบละเอียด",
};

// One audit row is always scoped to a single feature_toggle row (see `product` on
// WebsiteSystemAuditItem), so a changed start_datetime/end_datetime here is always
// rendered as a full Bangkok date+time. For a "schedule" (recurring) row that date
// is arbitrary/not meaningful - see scheduleForm.ts - but the diff has no reliable
// way to know a given entry's type_time (type_time is itself only present in the
// diff when it also changed), so this renders the stored value as-is rather than
// guessing; reported, not silently hidden.
export const formatAuditFieldValue = (field: string, value: unknown): string => {
  if (value === null || value === undefined || value === "") return "-";
  if (field === "is_enable" || field === "is_toast" || field === "is_notification") {
    return value ? "เปิด" : "ปิด";
  }
  // One audit row is one site (see `product`), so this says whether that site
  // was in the row's selected list.
  if (field === "is_selected") {
    return value ? "เลือก" : "ไม่เลือก";
  }
  if (field === "type_time" && typeof value === "string") {
    return TYPE_TIME_LABEL[value] || value;
  }
  if (field === "days_week" && typeof value === "string") {
    return value
      .split(",")
      .map((day) => DAY_LABEL[day] || day)
      .join(", ");
  }
  if ((field === "start_datetime" || field === "end_datetime") && typeof value === "string") {
    return datetime(value).format("DD/MM/YYYY HH:mm:ss");
  }
  return String(value);
};
